/**
 * CategoriesPage - Full categories directory organized by master categories
 * Shows all 75 categories grouped under their master category
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Package, ChevronRight, Layers, Search, X } from 'lucide-react';
import api from '../../services/api';
import { API_URL } from '../../config';
import {
  MarketplaceHeader,
  MarketplaceFooter,
  FloatingCartButton,
  MobileBottomNav,
} from '../../components/marketplace/MarketplaceLayout';

const COLORS = {
  navyBlue: '#1e3a5f',
  darkSlate: '#0f172a',
  orange: '#F37322',
};

const CategoriesPage = () => {
  const [searchParams] = useSearchParams();
  const preselectedMc = searchParams.get('mc');

  const [masterCategories, setMasterCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedMc, setSelectedMc] = useState(preselectedMc || 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadData();
  }, []);

  useEffect(() => {
    if (preselectedMc && !loading) {
      setSelectedMc(preselectedMc);
      const el = document.getElementById(`mc-${preselectedMc}`);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    }
  }, [preselectedMc, loading]);

  const loadData = async () => {
    try {
      const [catRes, mcRes] = await Promise.all([
        api.get('/marketplace/categories'),
        api.get('/master-categories/public'),
      ]);
      setCategories(catRes.data);
      setMasterCategories(mcRes.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const getImgSrc = (item) => {
    const url = item.thumbnail_url || item.image_url;
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  const getCatsForMaster = (mcId) =>
    categories.filter((c) => c.master_category_id === mcId);

  // Search filtering
  const normalizeText = (text) =>
    (text || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const q = normalizeText(searchQuery);
    return categories.filter(
      (cat) =>
        normalizeText(cat.name).includes(q) ||
        normalizeText(cat.product_examples).includes(q) ||
        normalizeText(cat.description).includes(q)
    );
  }, [searchQuery, categories]);

  const filteredMasterCategories =
    selectedMc === 'all'
      ? masterCategories
      : masterCategories.filter((mc) => mc.id === selectedMc);

  // When searching, show results grouped by master category
  const searchGrouped = useMemo(() => {
    if (!searchResults) return null;
    const groups = {};
    searchResults.forEach((cat) => {
      if (!groups[cat.master_category_id]) {
        const mc = masterCategories.find((m) => m.id === cat.master_category_id);
        groups[cat.master_category_id] = { mc, cats: [] };
      }
      groups[cat.master_category_id].cats.push(cat);
    });
    return Object.values(groups);
  }, [searchResults, masterCategories]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-50">
        <MarketplaceHeader />
        <main className="flex-1">
          <div className="bg-white border-b border-slate-100">
            <div className="max-w-7xl mx-auto px-4 py-6">
              <div className="h-5 w-32 bg-slate-200 rounded animate-pulse mb-3" />
              <div className="h-9 w-64 bg-slate-200 rounded animate-pulse mb-2" />
              <div className="h-4 w-48 bg-slate-100 rounded animate-pulse" />
            </div>
          </div>
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="flex gap-2 mb-8">
              {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                <div key={i} className="h-10 w-28 bg-slate-200 rounded-full animate-pulse" />
              ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => (
                <div key={i} className="bg-white rounded-xl border border-slate-100 p-4">
                  <div className="w-14 h-14 rounded-full bg-slate-100 animate-pulse mx-auto mb-3" />
                  <div className="h-4 w-20 bg-slate-100 rounded animate-pulse mx-auto mb-2" />
                  <div className="h-3 w-full bg-slate-50 rounded animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        </main>
        <MarketplaceFooter />
        <MobileBottomNav />
      </div>
    );
  }

  const renderCategoryCard = (cat) => (
    <Link
      key={cat.id}
      to={`/buscar?category=${cat.id}`}
      className="flex flex-col items-center gap-2 p-4 bg-white rounded-xl hover:shadow-lg hover:border-slate-200 transition-all group border border-slate-100"
    >
      <div className="w-16 h-16 rounded-full bg-slate-50 flex items-center justify-center overflow-hidden group-hover:scale-110 transition-transform">
        {getImgSrc(cat) ? (
          <img src={getImgSrc(cat)} alt={cat.name} className="w-full h-full object-cover" />
        ) : (
          <Package className="w-7 h-7 text-slate-300" />
        )}
      </div>
      <span
        className="text-xs sm:text-sm text-center font-semibold leading-tight"
        style={{ color: COLORS.darkSlate }}
      >
        {cat.name}
      </span>
      {cat.product_examples && (
        <p className="text-[10px] sm:text-xs text-slate-400 text-center leading-snug line-clamp-2">
          {cat.product_examples}
        </p>
      )}
    </Link>
  );

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <MarketplaceHeader />
      <main className="flex-1">
        {/* Page Header */}
        <div className="bg-white border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
              <Link to="/" className="hover:text-slate-700 transition-colors">Inicio</Link>
              <ChevronRight className="w-3 h-3" />
              <span style={{ color: COLORS.navyBlue }} className="font-medium">Categorias</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold" style={{ color: COLORS.darkSlate }}>
                  Todas las Categorias
                </h1>
                <p className="text-slate-500 mt-1">
                  Explora {categories.length} categorias en {masterCategories.length} sectores
                </p>
              </div>
              {/* Search input */}
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar categoria..."
                  className="w-full pl-9 pr-9 py-2.5 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-slate-300 outline-none transition-all"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-slate-100"
                  >
                    <X className="w-3.5 h-3.5 text-slate-400" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Filter Tabs - hidden when searching */}
        {!searchQuery && (
          <div className="bg-white border-b border-slate-100 sticky top-[60px] z-30">
            <div className="max-w-7xl mx-auto px-4">
              <div className="flex gap-1.5 overflow-x-auto py-3 scrollbar-hide">
                <button
                  onClick={() => setSelectedMc('all')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${
                    selectedMc === 'all'
                      ? 'text-white shadow-md'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  style={selectedMc === 'all' ? { backgroundColor: COLORS.navyBlue } : {}}
                >
                  <Layers className="w-4 h-4" />
                  Todas ({categories.length})
                </button>
                {masterCategories.map((mc) => {
                  const isActive = selectedMc === mc.id;
                  const mcImgSrc = getImgSrc(mc);
                  const count = getCatsForMaster(mc.id).length;
                  return (
                    <button
                      key={mc.id}
                      onClick={() => {
                        setSelectedMc(mc.id);
                        if (selectedMc !== 'all') {
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        } else {
                          const el = document.getElementById(`mc-${mc.id}`);
                          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${
                        isActive
                          ? 'text-white shadow-md'
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                      style={isActive ? { backgroundColor: COLORS.navyBlue } : {}}
                    >
                      {mcImgSrc ? (
                        <img
                          src={mcImgSrc}
                          alt={mc.name}
                          className={`w-5 h-5 rounded-full object-cover ${isActive ? 'ring-1 ring-white/50' : ''}`}
                        />
                      ) : (
                        <span className="text-sm">{mc.icon}</span>
                      )}
                      {mc.name}
                      <span className={`text-xs ${isActive ? 'text-white/70' : 'text-slate-400'}`}>
                        ({count})
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Search Results */}
        {searchQuery && (
          <div className="max-w-7xl mx-auto px-4 py-8">
            {searchResults && searchResults.length > 0 ? (
              <>
                <p className="text-sm text-slate-500 mb-6">
                  {searchResults.length} categoria{searchResults.length !== 1 ? 's' : ''} encontrada{searchResults.length !== 1 ? 's' : ''} para "<span className="font-medium text-slate-700">{searchQuery}</span>"
                </p>
                <div className="space-y-8">
                  {searchGrouped.map(({ mc, cats }) => (
                    <div key={mc?.id || 'unknown'}>
                      {mc && (
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-7 h-7 rounded-lg overflow-hidden flex items-center justify-center bg-slate-100 flex-shrink-0">
                            {getImgSrc(mc) ? (
                              <img src={getImgSrc(mc)} alt={mc.name} className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-sm">{mc.icon}</span>
                            )}
                          </div>
                          <span className="text-sm font-semibold text-slate-500">{mc.name}</span>
                        </div>
                      )}
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                        {cats.map(renderCategoryCard)}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="bg-white rounded-xl p-12 text-center border border-slate-200">
                <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">
                  No se encontraron categorias
                </h3>
                <p className="text-slate-500 mt-1 text-sm">
                  No hay resultados para "{searchQuery}". Intenta con otro termino.
                </p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-4 px-4 py-2 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
                  style={{ color: COLORS.navyBlue }}
                >
                  Ver todas las categorias
                </button>
              </div>
            )}
          </div>
        )}

        {/* Category Sections - hidden when searching */}
        {!searchQuery && (
          <div className="max-w-7xl mx-auto px-4 py-8 space-y-10">
            {filteredMasterCategories.map((mc, idx) => {
              const mcCats = getCatsForMaster(mc.id);
              if (mcCats.length === 0) return null;
              const mcImgSrc = getImgSrc(mc);

              return (
                <section key={mc.id} id={`mc-${mc.id}`} className="scroll-mt-32">
                  {/* Divider between sections (not on first) */}
                  {idx > 0 && selectedMc === 'all' && (
                    <div className="h-px bg-slate-200 mb-8" />
                  )}

                  {/* Section header */}
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center bg-white border border-slate-100 flex-shrink-0 shadow-sm">
                      {mcImgSrc ? (
                        <img src={mcImgSrc} alt={mc.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-2xl">{mc.icon}</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl font-bold" style={{ color: COLORS.darkSlate }}>
                        {mc.name}
                      </h2>
                      <p className="text-sm text-slate-400">
                        {mc.description || `${mcCats.length} categorias`}
                      </p>
                    </div>
                    <span className="hidden sm:inline-flex px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-500">
                      {mcCats.length} categorias
                    </span>
                  </div>

                  {/* Categories grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                    {mcCats.map(renderCategoryCard)}
                  </div>
                </section>
              );
            })}

            {filteredMasterCategories.length === 0 && (
              <div className="bg-white rounded-xl p-12 text-center border border-slate-200">
                <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">No se encontraron categorias</h3>
              </div>
            )}
          </div>
        )}
      </main>
      <MarketplaceFooter />
      <FloatingCartButton />
      <MobileBottomNav />
    </div>
  );
};

export default CategoriesPage;
